import { Box, Container, Heading, Text, VStack, useColorMode } from '@chakra-ui/react'

export function TermsPage() {
  const { colorMode } = useColorMode()

  return (
    <Container maxW="container.md" py={16}>
      <VStack spacing={8} align="stretch">
        <VStack spacing={4} align="start">
          <Heading as="h1" size="2xl">
            Terms of Service
          </Heading>
          <Text color="stone.500">
            Last updated: January 31, 2026
          </Text>
        </VStack>

        <Box
          p={8}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          bg={colorMode === 'dark' ? 'stone.800' : 'white'}
        >
          <VStack spacing={6} align="stretch">
            <Section
              title="1. Acceptance of Terms"
              content="By accessing and using myrkup.com (the 'Service'), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service."
            />

            <Section
              title="2. Description of Service"
              content="myrkup.com provides a secure markdown sharing platform with client-side encryption. The Service allows users to create, store, and share encrypted markdown content via shareable links."
            />

            <Section
              title="3. User Accounts and API Keys"
              content="To access certain features, you may need to create an account and generate API keys. You are responsible for maintaining the confidentiality of your API keys and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account."
            />

            <Section
              title="4. Acceptable Use"
              content="You agree not to use the Service to store, share, or transmit any content that: (a) violates any applicable law or regulation; (b) infringes on the intellectual property rights of others; (c) contains malware, viruses, or other harmful code; (d) is defamatory, obscene, or otherwise objectionable; or (e) interferes with the proper functioning of the Service."
            />

            <Section
              title="5. Content and Encryption"
              content="All content shared through the Service is encrypted with AES-256-GCM before storage. Whether content is submitted through the browser (client-side encryption), MCP, or the REST API, the server only stores ciphertext. You are solely responsible for maintaining your passcodes and encryption keys. Loss of passcodes may result in permanent loss of access to your content."
            />

            <Section
              title="6. Free and Pro Tiers"
              content="The Service is offered in Free and Pro tiers with different limits on storage, shares, and API usage. We reserve the right to modify tier limits and pricing at any time with reasonable notice."
            />

            <Section
              title="7. Data Retention and Deletion"
              content="Shares may be configured with expiration dates. Expired shares are automatically deleted. We reserve the right to delete inactive or abandoned content after reasonable notice. You may delete your shares at any time through the management interface."
            />

            <Section
              title="8. API Usage Limits"
              content="API access is subject to rate limits based on your account tier. Exceeding these limits may result in temporary suspension of API access. Systematic abuse of the API may result in permanent account termination."
            />

            <Section
              title="9. Intellectual Property"
              content="The Service, including its design, features, and functionality, is owned by myrkup.com and is protected by copyright and other intellectual property laws. You retain ownership of all content you upload to the Service."
            />

            <Section
              title="10. Disclaimer of Warranties"
              content="THE SERVICE IS PROVIDED 'AS IS' WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. YOU USE THE SERVICE AT YOUR OWN RISK."
            />

            <Section
              title="11. Limitation of Liability"
              content="TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, OR GOODWILL ARISING OUT OF YOUR USE OF THE SERVICE."
            />

            <Section
              title="12. Termination"
              content="We reserve the right to suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service will immediately cease."
            />

            <Section
              title="13. Changes to Terms"
              content="We may modify these Terms of Service at any time. We will notify users of material changes by updating the 'Last updated' date. Your continued use of the Service after changes constitutes acceptance of the modified terms."
            />

            <Section
              title="14. Governing Law"
              content="These Terms of Service shall be governed by and construed in accordance with the laws of the jurisdiction in which myrkup.com operates, without regard to conflict of law principles."
            />

            <Section
              title="15. Contact"
              content="If you have any questions about these Terms of Service, please contact us through our support channels."
            />
          </VStack>
        </Box>
      </VStack>
    </Container>
  )
}

function Section({ title, content }: { title: string; content: string }) {
  return (
    <VStack spacing={2} align="stretch">
      <Heading as="h2" size="md">
        {title}
      </Heading>
      <Text color="stone.600" _dark={{ color: 'stone.300' }} lineHeight="tall">
        {content}
      </Text>
    </VStack>
  )
}
